import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { useAuth } from '@/hooks/useAuth';
import { useLabOrder, useCancelLabOrder } from './hooks';
import { LabStatusBadge, OutsideLabBadge, PriorityBadge, Field, fmtDateTime, rejectionLabel } from './ui';
import SampleActions from './SampleActions';
import OutsideLabItemDialog from './OutsideLabItemDialog';
import ResultEntry from './ResultEntry';
import ReportPanel from './ReportPanel';

/**
 * One order, everything about it: its tests, its tubes, its results and its
 * report — the screen someone opens when a patient or a clinician asks "where
 * is my blood test".
 *
 * Deliberately one dialog rather than a page, because it is always reached
 * from a work queue and closing it should put the user back in the queue with
 * their filters intact. Same reasoning IPModule's own drill-throughs use.
 */
export interface LabOrderDetailsProps {
  orderId: number | null;
  onClose: () => void;
}

export default function LabOrderDetails({ orderId, onClose }: LabOrderDetailsProps) {
  const { user } = useAuth();
  const { data: order, isLoading, error, refetch } = useLabOrder(orderId);
  const cancel = useCancelLabOrder(orderId ?? 0);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [openResultItem, setOpenResultItem] = useState<number | null>(null);
  const [openOutsideItem, setOpenOutsideItem] = useState<number | null>(null);

  const canCancel = !!user?.modules?.includes('lab:orders');

  const doCancel = async () => {
    setCancelError(null);
    try {
      await cancel.mutateAsync({ reason: cancelReason.trim() });
      setCancelling(false);
      setCancelReason('');
    } catch (e) { setCancelError((e as Error).message); }
  };

  return (
    <>
      <Dialog open={orderId !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-4xl">
          {isLoading && <LoadingState label="Loading order…" />}
          {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}
          {order && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span className="font-mono">{order.order_number}</span>
                  <LabStatusBadge status={order.status} />
                  <PriorityBadge priority={order.priority} />
                  {order.has_critical && <Badge variant="danger">Critical result</Badge>}
                </DialogTitle>
              </DialogHeader>

              <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label="Patient">{order.patient_name} <span className="text-muted-foreground">({order.uhid})</span></Field>
                  <Field label="Age / Sex">{order.age ?? '—'}{order.age ? 'y' : ''}, {order.gender}</Field>
                  <Field label="Encounter">{order.visit_code ?? (order.admission_id ? `IP admission #${order.admission_id}` : '—')}</Field>
                  <Field label="Ordered by">{order.ordering_doctor_name ?? '—'}</Field>
                  <Field label="Ordered at">{fmtDateTime(order.ordered_at)}</Field>
                  <Field label="Billing">
                    {order.bill_id
                      ? <Badge variant="success">Billed</Badge>
                      : <span className="text-muted-foreground">Not billed separately</span>}
                  </Field>
                  <Field label="Diagnosis">{order.diagnosis ?? '—'}</Field>
                  <Field label="Clinical notes">{order.clinical_notes ?? '—'}</Field>
                </div>

                {order.cancellation_reason && (
                  <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px]">
                    <span className="font-semibold">Cancelled:</span> {order.cancellation_reason}
                  </div>
                )}

                {/* ── Tests ─────────────────────────────────────── */}
                <section>
                  <h3 className="mb-1.5 text-sm font-semibold">Tests</h3>
                  <div className="rounded-xl border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Test</TableHead>
                          <TableHead>Department</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Entered / verified</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {order.items.map((it) => (
                          <TableRow key={it.id}>
                            <TableCell>
                              <div className="font-medium">
                                {it.test_name}
                                <OutsideLabBadge processingType={it.processing_type} referenceLabName={it.reference_lab_name} />
                              </div>
                              <div className="font-mono text-[11px] text-muted-foreground">{it.test_code}</div>
                            </TableCell>
                            <TableCell className="text-[12px]">{it.department_name ?? '—'}</TableCell>
                            <TableCell>
                              <LabStatusBadge status={it.status} processingType={it.processing_type} />
                              {it.has_critical && <Badge variant="danger" className="ml-1">Critical</Badge>}
                              {!it.has_critical && it.has_abnormal && <Badge variant="warn" className="ml-1">Abnormal</Badge>}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground">
                              {it.entered_by ? <div>Entered: {it.entered_by} · {fmtDateTime(it.entered_at)}</div> : null}
                              {it.verified_by ? <div>Verified: {it.verified_by} · {fmtDateTime(it.verified_at)}</div> : null}
                              {!it.entered_by && !it.verified_by ? '—' : null}
                            </TableCell>
                            <TableCell className="text-right">
                              {it.processing_type === 'outsourced' ? (
                                <Button variant="link" size="sm" onClick={() => setOpenOutsideItem(it.id)}>Outside lab</Button>
                              ) : (
                                <Button variant="link" size="sm" onClick={() => setOpenResultItem(it.id)}>
                                  {it.result_status === 'verified' || it.status === 'REPORTED' ? 'View result' : 'Result'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </section>

                {/* ── Samples ───────────────────────────────────── */}
                <section>
                  <h3 className="mb-1.5 text-sm font-semibold">Samples</h3>
                  <div className="space-y-2">
                    {order.samples.map((s) => (
                      <div key={s.id} className="rounded-xl border border-border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-[13px]">
                            <span className="font-medium">{s.sample_type_name ?? 'Sample'}</span>
                            {s.container_name && <span className="text-muted-foreground"> · {s.container_name}</span>}
                            {s.barcode && <span className="ml-2 font-mono text-[11px]">{s.barcode}</span>}
                            {s.recollection_of_id && (
                              <Badge variant="info" className="ml-2">Recollection of #{s.recollection_of_id}</Badge>
                            )}
                          </div>
                          <LabStatusBadge status={s.status} />
                        </div>
                        <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground sm:grid-cols-4">
                          <span>Collected: {s.collected_at ? `${fmtDateTime(s.collected_at)} · ${s.collected_by}` : '—'}</span>
                          <span>Received: {s.received_at ? `${fmtDateTime(s.received_at)} · ${s.received_by}` : '—'}</span>
                          <span>Location: {s.collection_location ?? '—'}</span>
                          <span>
                            {s.rejection_reason
                              ? `Rejected: ${rejectionLabel(s.rejection_reason)}${s.rejection_remarks ? ` — ${s.rejection_remarks}` : ''}`
                              : ''}
                          </span>
                        </div>
                        <div className="mt-2">
                          <SampleActions sample={{ ...s, order_number: order.order_number, lab_order_id: order.id, patient_name: order.patient_name, uhid: order.uhid }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ── Report ────────────────────────────────────── */}
                <ReportPanel orderId={order.id} uhid={order.uhid} />

                {/* ── Cancel ────────────────────────────────────── */}
                {canCancel && order.status !== 'CANCELLED' && order.status !== 'REPORTED' && (
                  <section className="rounded-xl border border-border p-3">
                    {!cancelling ? (
                      <Button variant="ghost" size="sm" onClick={() => setCancelling(true)}>Cancel this order</Button>
                    ) : (
                      <div className="space-y-2">
                        <Textarea
                          rows={2}
                          placeholder="Why is this being cancelled? (required, and recorded on the order)"
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                        />
                        <p className="text-[11px] text-muted-foreground">
                          A test that has already been verified cannot be cancelled — the result exists and stays
                          on the record. Reverse the money with a credit note against the bill instead.
                        </p>
                        {cancelError && <ErrorState message={cancelError} />}
                        <div className="flex gap-2">
                          <Button variant="danger" size="sm" disabled={!cancelReason.trim() || cancel.isPending} onClick={doCancel}>
                            {cancel.isPending ? 'Cancelling…' : 'Confirm cancellation'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setCancelling(false); setCancelError(null); }}>
                            Keep the order
                          </Button>
                        </div>
                      </div>
                    )}
                  </section>
                )}
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={onClose}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ResultEntry
        orderItemId={openResultItem}
        orderId={orderId ?? undefined}
        onClose={() => setOpenResultItem(null)}
      />
      <OutsideLabItemDialog orderItemId={openOutsideItem} onClose={() => setOpenOutsideItem(null)} />
    </>
  );
}
